import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useQueryGetExamAssignment } from "@/apis/exam_assignments";
import { useQueryGetExamSet } from "@/apis/exam_sets/getExamSet";
import { getExamPaper } from "@/apis/exam_papers/getExamPaper";
import { QueryKeys } from "@/lib/query-keys";
import PrintableQuestionsView from "@/components/exam/PrintableQuestionsView";
import { Spinner } from "@/components/spinner";
import { Listbox, ListboxOption, ListboxLabel } from "@/components/listbox";
import { Button } from "@/components/button";
import { getPaperTypeLabel } from "@/constants/exam";
import FilteringQuestionsModal from "../exam_papers/components/FilteringQuestionsModal";
import type { IQuestion } from "@/apis/exam_papers/types";
import type { SubjectType } from "@/apis/exam_progress/types";

// Extended question type with module information
interface IQuestionWithModule extends IQuestion {
  moduleIndex: number;
  moduleQuestionIndex: number;
}

const ExamAssignmentPrintQuestionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedSubject, setSelectedSubject] = useState<SubjectType | "">("");
  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Get exam assignment
  const {
    data: examAssignment,
    isLoading: isLoadingAssignment,
    error: assignmentError,
  } = useQueryGetExamAssignment(id!);

  // Get exam set
  const { data: examSet, isLoading: isLoadingExamSet } = useQueryGetExamSet(
    examAssignment?.exam_set_id || "",
    {
      enabled: !!examAssignment?.exam_set_id,
    }
  );

  // Get available subjects from exam set
  const availableSubjects: SubjectType[] = examSet?.items
    ? examSet.items
        .map((item) => item.exam_paper?.subject)
        .filter((subject): subject is SubjectType => !!subject)
        .filter((subject, index, array) => array.indexOf(subject) === index) // Remove duplicates
    : [];

  // Set default subject to first available subject
  useEffect(() => {
    if (availableSubjects.length > 0 && !selectedSubject) {
      setSelectedSubject(availableSubjects[0]);
    }
  }, [availableSubjects]);

  // Get exam paper for selected subject
  const selectedExamPaper = examSet?.items?.find(
    (item) => item.exam_paper?.subject === selectedSubject
  )?.exam_paper;

  const {
    data: examPaperWithQuestions,
    isLoading: isLoadingPaper,
    error: paperError,
  } = useQuery({
    queryKey: [QueryKeys.examPaper, selectedExamPaper?.id],
    queryFn: () => getExamPaper(selectedExamPaper!.id),
    enabled: !!selectedExamPaper?.id,
  });

  // Collect questions with module information
  const allQuestions = useMemo((): IQuestionWithModule[] => {
    if (!examPaperWithQuestions) return [];

    const questions: IQuestionWithModule[] = [];

    if (selectedModule === "all") {
      examPaperWithQuestions.sections.forEach((section, sectionIndex) => {
        if (section.questions) {
          section.questions.forEach((question, questionIndex) => {
            questions.push({
              ...question,
              moduleIndex: sectionIndex + 1,
              moduleQuestionIndex: questionIndex + 1,
            });
          });
        }
      });
    } else {
      const moduleIndex = parseInt(selectedModule) - 1;
      const selectedSection = examPaperWithQuestions.sections[moduleIndex];
      if (selectedSection?.questions) {
        selectedSection.questions.forEach((question, questionIndex) => {
          questions.push({
            ...question,
            moduleIndex: moduleIndex + 1,
            moduleQuestionIndex: questionIndex + 1,
          });
        });
      }
    }

    return questions;
  }, [examPaperWithQuestions, selectedModule]);

  // Filter questions based on selection
  const questionsToShow = selectedQuestionIds.length > 0
    ? allQuestions.filter((q) => selectedQuestionIds.includes(q.id))
    : allQuestions;

  // Prepare subject options for the dropdown
  const subjectOptions = availableSubjects.map((subject) => ({
    value: subject,
    label: getPaperTypeLabel(subject),
  }));

  // Prepare module options for the dropdown
  const moduleOptions = examPaperWithQuestions
    ? [
        { value: "all", label: "All Modules" },
        ...examPaperWithQuestions.sections.map((_, index) => ({
          value: String(index + 1),
          label: `Module ${index + 1}`,
        })),
      ]
    : [];

  // Reset and select all questions when module or subject changes
  useEffect(() => {
    if (allQuestions.length > 0) {
      setSelectedQuestionIds(allQuestions.map((q) => q.id));
    }
  }, [allQuestions]);

  // Initialize question selection when data loads
  useEffect(() => {
    if (allQuestions.length > 0 && selectedQuestionIds.length === 0) {
      setSelectedQuestionIds(allQuestions.map((q) => q.id));
    }
  }, [allQuestions, selectedQuestionIds.length]);

  const handleQuestionFilterApply = (selectedIds: string[]) => {
    setSelectedQuestionIds(selectedIds);
  };

  const isLoading = isLoadingAssignment || isLoadingExamSet || isLoadingPaper;
  const error = assignmentError || paperError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Exam Assignment
          </h2>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
        </div>
      </div>
    );
  }

  if (!examAssignment || !examSet) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Exam Assignment Not Found
          </h2>
          <p className="text-gray-500">
            The requested exam assignment could not be found.
          </p>
        </div>
      </div>
    );
  }

  if (availableSubjects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            No Subjects Available
          </h2>
          <p className="text-gray-500">
            This exam assignment does not contain any subjects to print.
          </p>
        </div>
      </div>
    );
  }

  if (allQuestions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            No Questions Available
          </h2>
          <p className="text-gray-500">
            The selected subject does not contain any questions to print.
          </p>
        </div>
      </div>
    );
  }

  if (questionsToShow.length === 0) {
    return (
      <div>
        {/* Filters - Hidden when printing */}
        <div className="print:hidden mb-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Select Subject:
            </label>
            <Listbox
              value={selectedSubject}
              onChange={setSelectedSubject}
              className="flex-1 max-w-xs"
            >
              {subjectOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>

            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Select Module:
            </label>
            <Listbox
              value={selectedModule}
              onChange={setSelectedModule}
              className="flex-1 max-w-xs"
            >
              {moduleOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>

            <Button
              onClick={() => setIsFilterModalOpen(true)}
              className="whitespace-nowrap"
            >
              Select Questions ({selectedQuestionIds.length}/
              {allQuestions.length})
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              No Questions Selected
            </h2>
            <p className="text-gray-500">
              Please select questions to print using the "Select Questions"
              button.
            </p>
          </div>
        </div>

        {/* Filter Modal */}
        <FilteringQuestionsModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleQuestionFilterApply}
          questions={allQuestions}
          selectedQuestionIds={selectedQuestionIds}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Filters - Hidden when printing */}
      <div className="print:hidden mb-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-4">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Select Subject:
          </label>
          <Listbox
            value={selectedSubject}
            onChange={setSelectedSubject}
            className="flex-1 max-w-xs"
          >
            {subjectOptions.map((option) => (
              <ListboxOption key={option.value} value={option.value}>
                <ListboxLabel>{option.label}</ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>

          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Select Module:
          </label>
          <Listbox
            value={selectedModule}
            onChange={setSelectedModule}
            className="flex-1 max-w-xs"
          >
            {moduleOptions.map((option) => (
              <ListboxOption key={option.value} value={option.value}>
                <ListboxLabel>{option.label}</ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>

          <Button
            onClick={() => setIsFilterModalOpen(true)}
            className="whitespace-nowrap"
          >
            Select Questions ({selectedQuestionIds.length}/{allQuestions.length}
            )
          </Button>
        </div>
      </div>

      {/* Printable Questions */}
      <PrintableQuestionsView
        questions={questionsToShow}
        title={`${examAssignment.title} - ${getPaperTypeLabel(selectedSubject)}${
          selectedModule !== "all" ? ` (Module ${selectedModule})` : ""
        }`}
        examType={examSet.exam_type}
        subject={selectedSubject}
      />

      {/* Filter Modal */}
      <FilteringQuestionsModal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleQuestionFilterApply}
        questions={allQuestions}
        selectedQuestionIds={selectedQuestionIds}
      />
    </div>
  );
};

export default ExamAssignmentPrintQuestionsPage; 